import { useEffect, useState } from 'react';

import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../../../backend/src/organisation/organisation-permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useUpdateMatter } from '@/api/mutations/useUpdateMatter';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRun } from '@/helpers/runs';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsRow,
  SettingsSaveButton,
  SettingsSaveStatus,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { DeleteMatterDialog } from '@/new/pages/matters/components/delete-matter-dialog';
import { MATTER_LANGUAGE_LABELS } from '@/new/pages/matters/new/:matterId/basic';

const OptionalTag = () => <span className="font-normal text-gray-400">(optional)</span>;

export function Metadata() {
  const { matterId } = useParams();
  if (!matterId) return null;

  return <MetadataContent matterId={matterId} />;
}

function MetadataContent({ matterId }: { matterId: string }) {
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { isEnabled } = useOrgPermissions();
  const languageEnabled = isEnabled(OrgPermission.MatterLanguage);
  const { data: matter } = useGetMatterV2(matterId);
  const { data: runs } = useGetMatterRuns(matterId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutateAsync: updateMatter } = useUpdateMatter(matterId);

  // Local, editable copies of the matter metadata fields.
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [pageLimit, setPageLimit] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Seed local state once the matter loads (and re-seed after a successful save
  // invalidates the query).
  useEffect(() => {
    if (!matter) return;
    setName(matter.name);
    setCode(matter.code ?? '');
    setPageLimit(matter.tokenLimit?.toString() ?? '');
  }, [matter]);

  // matter:edit and matter:delete resolve through the override system.
  // Seat-based creators can edit/delete via escalation;
  // non-seat-based creators must have explicit permissions.
  const isCreator = matter?.createdById === user?.id;
  const isSeatBasedCreator = isCreator && matter?.isSeatBased;
  const canEdit = hasPermission(Permission.MatterEdit) || isSeatBasedCreator;
  const canDelete = hasPermission(Permission.MatterDelete) || isSeatBasedCreator;

  // Check if there are any ongoing runs
  const hasOngoingRuns = hasAnyOngoingRun(runs);

  const originalName = matter?.name ?? '';
  const originalCode = matter?.code ?? '';
  const originalPageLimit = matter?.tokenLimit?.toString() ?? '';
  const hasUnsavedChanges = name !== originalName || code !== originalCode || pageLimit !== originalPageLimit;

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Matter name cannot be empty or just spaces.');
      return;
    }

    const trimmedPageLimit = pageLimit.trim();
    let tokenLimit: number | null = null;
    if (trimmedPageLimit) {
      const parsed = Number(trimmedPageLimit);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        toast.error('Matter page limit must be a positive whole number or empty.');
        return;
      }
      tokenLimit = parsed;
    }

    setIsSaving(true);
    try {
      await updateMatter({
        name: trimmedName,
        code: code.trim() || undefined,
        tokenLimit,
      });
      trackCustomEvent(TrackingEventsV2.MatterEdit, { matterId });
      toast.success('Matter metadata updated successfully');
    } catch {
      // useUpdateMatter surfaces its own error toast.
    } finally {
      setIsSaving(false);
    }
  };

  if (!matter) return null;

  return (
    <SettingsContent>
      <SettingsSection
        title="Matter metadata"
        description="View and update your matter name and metadata information."
        footer={
          <SettingsSectionFooter>
            <SettingsSaveStatus variant={hasUnsavedChanges ? 'unsaved' : 'saved'}>
              {hasUnsavedChanges ? 'You have unsaved changes' : 'All changes saved'}
            </SettingsSaveStatus>
            {canEdit ? (
              <SettingsSaveButton
                isLoading={isSaving}
                hasUnsavedChanges={hasUnsavedChanges}
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <SettingsSaveButton hasUnsavedChanges={false} disabled />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p>You don&apos;t have permission to edit this Matter. Ask a Matter admin or an org administrator.</p>
                </TooltipContent>
              </Tooltip>
            )}
          </SettingsSectionFooter>
        }
      >
        <SettingsRow title="Matter name">
          <Input
            className="w-64"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter matter name..."
            disabled={!canEdit}
          />
        </SettingsRow>

        <SettingsRow
          title={
            <>
              Matter code <OptionalTag />
            </>
          }
        >
          <Input
            className="w-64 font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="WEX-123"
            disabled={!canEdit}
          />
        </SettingsRow>

        <SettingsRow
          title={
            <>
              Matter page limit <OptionalTag />
            </>
          }
          description="The maximum number of pages that can be processed in this Matter before always requiring approval."
        >
          <Input
            type="number"
            className="w-28 text-right"
            value={pageLimit}
            onChange={(e) => setPageLimit(e.target.value)}
            placeholder="e.g. 25000"
            min="1"
            step="1000"
            disabled={!canEdit}
          />
        </SettingsRow>

        {languageEnabled && (
          <SettingsRow
            title={
              <span className="flex items-center gap-1.5">
                Matter language
                <TooltipIcon
                  side="right"
                  tooltipContent="The primary language of this Matter. This is set when the Matter is created and cannot be changed afterwards. Facts, inconsistencies, and entities are generated in this language."
                  contentClassName="max-w-md"
                />
              </span>
            }
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Input
                    className="w-64"
                    value={MATTER_LANGUAGE_LABELS[matter.language]}
                    disabled
                    readOnly
                    data-testid="matter-settings-language-field"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-lg">
                <p>The Matter language cannot be changed after the Matter has been created.</p>
              </TooltipContent>
            </Tooltip>
          </SettingsRow>
        )}

        {matter.description && (
          <SettingsRow title="Description">
            <span className="max-w-md text-right text-sm text-gray-800">{matter.description}</span>
          </SettingsRow>
        )}

        {matter.createdDate && (
          <SettingsRow title="Date created">
            <span className="flex items-center gap-2 text-sm text-gray-800">
              {formatDate(matter.createdDate, true, true)}
            </span>
          </SettingsRow>
        )}

        <SettingsRow title="Created by">
          <span className="text-sm text-gray-800">{matter.createdBy?.email || 'Unknown'}</span>
        </SettingsRow>

        <SettingsRow title="Matter ID">
          <span className="font-mono text-sm text-gray-800">{matter.id}</span>
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title="Danger zone">
        <SettingsRow
          title="Delete matter"
          description="Permanently delete this matter, all subsets, documents, and associated data."
        >
          {canDelete && !hasOngoingRuns ? (
            <Button variant="destructiveGhost" onClick={() => setDeleteDialogOpen(true)}>
              <Icon className="lucide--trash-2" />
              Delete Matter
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button variant="destructiveGhost" disabled>
                    <Icon className="lucide--trash-2" />
                    Delete Matter
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p>
                  {hasOngoingRuns
                    ? 'Cannot delete matter while processing is ongoing'
                    : "You don't have permission to delete this Matter. Ask a Matter admin or an org administrator."}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </SettingsRow>

        <SettingsRow
          title="Delete documents"
          description={
            <p className="inline-flex flex-wrap items-center gap-1.5">
              Delete documents by selecting them on the documents page and clicking
              <span className="inline-flex items-center rounded-md border bg-gray-50 px-0.5 py-1 align-middle text-xs">
                <Icon className="lucide--more-vertical text-gray-700" />
              </span>
            </p>
          }
        >
          {matter.allDocumentsCaseId && (
            <Link
              to={`/matters/${matterId}/subsets/${matter.allDocumentsCaseId}/docs`}
              className="flex items-center gap-1 text-sm font-medium text-blue-500 hover:text-blue-600"
            >
              Documents
              <Icon className="lucide--arrow-up-right h-4 w-4" />
            </Link>
          )}
        </SettingsRow>
      </SettingsSection>

      <DeleteMatterDialog
        matterId={matterId}
        matterName={matter.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        redirectOnSuccess
      />
    </SettingsContent>
  );
}
